import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import Button from '../components/Button';
import Icon from '../components/Icon';

type Step = 'email' | 'otp' | 'done';

export default function ForgotPassword() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');

    const requestOtp = async (e?: FormEvent) => {
        e?.preventDefault();
        setError(''); setInfo('');
        const trimmed = email.trim();
        if (!trimmed || !trimmed.includes('@')) {
            setError(t('auth.forgot.errors.invalidEmail'));
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE}/auth/forgot-password`, { email: trimmed });
            setInfo(res.data?.message || t('auth.forgot.defaultSent'));
            setStep('otp');
        } catch (err: unknown) {
            const e = err as { response?: { status?: number } };
            if (e.response?.status === 429) {
                setError(t('auth.forgot.errors.tooManyRequests'));
            } else {
                setError(t('auth.forgot.errors.connection'));
            }
        } finally {
            setLoading(false);
        }
    };

    const submitReset = async (e?: FormEvent) => {
        e?.preventDefault();
        setError(''); setInfo('');
        const code = otp.trim();
        if (code.length !== 6 || !/^\d{6}$/.test(code)) {
            setError(t('auth.forgot.errors.invalidOtp'));
            return;
        }
        if (newPassword.length < 8) {
            setError(t('auth.forgot.errors.weakPassword'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('auth.forgot.errors.mismatch'));
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API_BASE}/auth/reset-password`, {
                email: email.trim(),
                otp: code,
                newPassword,
            });
            setStep('done');
        } catch (err: unknown) {
            const e = err as { response?: { status?: number; data?: { message?: string } } };
            if (e.response?.status === 401) {
                setError(t('auth.forgot.errors.invalidCode'));
            } else if (e.response?.status === 429) {
                setError(t('auth.forgot.errors.tooManyAttempts'));
            } else {
                setError(e.response?.data?.message || t('auth.forgot.errors.resetFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                minHeight: '100dvh',
                backgroundColor: colors.secondary,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '24px max(24px, env(safe-area-inset-left))',
            }}
        >
            <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{ color: '#f8fafc', fontSize: 22, fontWeight: 900 }}>
                        {t('auth.forgot.title')}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 8 }}>
                        {step === 'email' && t('auth.forgot.subtitleEmail')}
                        {step === 'otp' && t('auth.forgot.subtitleOtp')}
                        {step === 'done' && t('auth.forgot.subtitleDone')}
                    </div>
                </div>

                <form
                    onSubmit={step === 'email' ? requestOtp : submitReset}
                    style={{
                        backgroundColor: colors.secondaryRaised,
                        borderRadius: 16,
                        padding: 22,
                        border: `1px solid ${colors.borderStrong}`,
                    }}
                >
                    {error ? (
                        <div style={alertStyle(colors.dangerSoft, colors.danger)}>
                            <Icon name="exclamation-circle" size={14} color={colors.dangerText} />
                            <div style={{ color: colors.dangerText, fontSize: 13, flex: 1 }}>{error}</div>
                        </div>
                    ) : null}
                    {info && !error ? (
                        <div style={alertStyle(colors.surfaceMuted, colors.cardBorder)}>
                            <Icon name="info-circle" size={14} color={colors.accentDark} />
                            <div style={{ color: '#cbd5e1', fontSize: 13, flex: 1 }}>{info}</div>
                        </div>
                    ) : null}

                    {step === 'email' && (
                        <>
                            <Field label={t('auth.forgot.email')}>
                                <Icon name="envelope" size={14} color="#475569" style={{ paddingLeft: 14, paddingRight: 10 }} />
                                <input
                                    style={inputStyle}
                                    placeholder={t('auth.forgot.emailPlaceholder')}
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    autoComplete="email"
                                    autoCapitalize="none"
                                />
                            </Field>
                            <Button
                                label={t('auth.forgot.sendCode')}
                                icon="paper-plane"
                                loading={loading}
                                type="submit"
                                style={{ marginTop: 22 }}
                            />
                        </>
                    )}

                    {step === 'otp' && (
                        <>
                            <Field label={t('auth.forgot.otpLabel')}>
                                <Icon name="key" size={14} color="#475569" style={{ paddingLeft: 14, paddingRight: 10 }} />
                                <input
                                    style={inputStyle}
                                    placeholder={t('auth.forgot.otpPlaceholder')}
                                    inputMode="numeric"
                                    pattern="\d{6}"
                                    maxLength={6}
                                    value={otp}
                                    onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                                    autoComplete="one-time-code"
                                />
                            </Field>
                            <Field label={t('auth.forgot.newPassword')}>
                                <Icon name="lock" size={14} color="#475569" style={{ paddingLeft: 14, paddingRight: 10 }} />
                                <input
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder={t('auth.forgot.newPasswordPlaceholder')}
                                    type={showPass ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    aria-label={showPass ? t('auth.forgot.hidePassword') : t('auth.forgot.showPassword')}
                                    onClick={() => setShowPass(v => !v)}
                                    style={eyeBtnStyle}
                                >
                                    <Icon name={showPass ? 'eye-slash' : 'eye'} size={14} color="#475569" />
                                </button>
                            </Field>
                            <Field label={t('auth.forgot.confirmPassword')}>
                                <Icon name="lock" size={14} color="#475569" style={{ paddingLeft: 14, paddingRight: 10 }} />
                                <input
                                    style={inputStyle}
                                    placeholder={t('auth.forgot.confirmPasswordPlaceholder')}
                                    type={showPass ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                            </Field>
                            <Button
                                label={t('auth.forgot.resetPassword')}
                                icon="check"
                                loading={loading}
                                type="submit"
                                style={{ marginTop: 22 }}
                            />
                            <button
                                type="button"
                                onClick={() => { setStep('email'); setOtp(''); setNewPassword(''); setConfirmPassword(''); setError(''); setInfo(''); }}
                                style={linkBtnStyle('#94a3b8')}
                            >
                                {t('auth.forgot.useDifferentEmail')}
                            </button>
                        </>
                    )}

                    {step === 'done' && (
                        <>
                            <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
                                <Icon name="check-circle" size={36} color={colors.accentDark} />
                                <div style={{ color: '#f8fafc', fontSize: 15, fontWeight: 600, marginTop: 12 }}>
                                    {t('auth.forgot.passwordUpdated')}
                                </div>
                                <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>
                                    {t('auth.forgot.passwordUpdatedSub')}
                                </div>
                            </div>
                            <Button
                                label={t('auth.forgot.backToLoginButton')}
                                icon="sign-in-alt"
                                onClick={() => navigate('/login')}
                                style={{ marginTop: 22 }}
                            />
                        </>
                    )}
                </form>

                {step !== 'done' && (
                    <div style={{ textAlign: 'center', marginTop: 22 }}>
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            style={linkBtnStyle(colors.accent)}
                        >
                            {t('auth.forgot.backToLogin')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div>
            <div
                style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#94a3b8',
                    marginBottom: 6,
                    marginTop: 14,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                }}
            >
                {label}
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#0f172a',
                    borderRadius: 8,
                    border: '1px solid #334155',
                    minHeight: 50,
                }}
            >
                {children}
            </div>
        </div>
    );
}

const inputStyle: React.CSSProperties = {
    flex: 1,
    padding: '13px 14px 13px 0',
    fontSize: 15,
    color: '#f8fafc',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    width: '100%',
};

const eyeBtnStyle: React.CSSProperties = {
    width: 50,
    minHeight: 50,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const alertStyle = (bg: string, border: string): React.CSSProperties => ({
    backgroundColor: bg,
    borderRadius: 8,
    padding: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
    border: `1px solid ${border}`,
});

const linkBtnStyle = (color: string): React.CSSProperties => ({
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color,
    fontSize: 13,
    fontWeight: 600,
    padding: '10px 0',
    marginTop: 14,
});
